import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";
import {
  CommonRoutesConst,
  LoginRoutesConst,
  LogoutRoutesConst,
  StepParamConst,
} from "src/types/constType";
import { useKidsStore } from "stores/useKidsStore";

const CommonRoutes: RouteRecordRaw[] = [
  {
    path: "/policy/:type",
    name: CommonRoutesConst.Policy,
    props: true,
    component: () =>
      import(/* webpackChunkName: "views-Policy" */ "pages/common/Policy.vue"),
    meta: {
      title: "서비스 이용약관",
    },
  },

  {
    path: "/exit",
    name: CommonRoutesConst.Exit,
    component: () =>
      import(/* webpackChunkName: "views-Exit" */ "pages/common/Exit.vue"),
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: "/:catchAll(.*)*",
    redirect: "/",
  },
];

function hasKids(
  state: boolean,
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const kidsStore = useKidsStore();

  if (state && !kidsStore.getKids.length) {
    next({
      replace: true,
      name: LoginRoutesConst.AddFirstKid,
      params: { step: StepParamConst.kidInfo },
    });
    return;
  }
  if (
    !state &&
    kidsStore.getKids.length &&
    to.params.step !== StepParamConst.qrcode
  ) {
    next({
      replace: true,
      name: LoginRoutesConst.Dashboard,
    });
    return;
  }

  next();
}

const LogoutRoutes: RouteRecordRaw[] = [
  {
    path: "/walkthrough",
    name: LogoutRoutesConst.Walkthrough,
    component: () =>
      import(
        /* webpackChunkName: "views-Walkthrough" */ "pages/LogoutLayout/Walkthrough.vue"
      ),
  },
  {
    path: "/signIn",
    name: LogoutRoutesConst.SignIn,
    component: () =>
      import(
        /* webpackChunkName: "views-SignIn" */ "pages/LogoutLayout/SignIn.vue"
      ),
  },
];
const LoginRoutes: RouteRecordRaw[] = [
  {
    path: "",
    name: LoginRoutesConst.Dashboard,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-Dashboard" */ "pages/LoginLayout/Dashboard.vue"
      ),
  },
  {
    path: "/dashboard/taskHelpingResult",
    name: LoginRoutesConst.TaskHelpingResult,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "뽀미 도와주기 결과",
      type: "taskHelpingResult",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-ParentSessionInfo" */ "pages/LoginLayout/Dashboard/TaskHelpingResult.vue"
      ),
  },
  {
    path: "/dashboard/mykidpromise",
    name: LoginRoutesConst.MyKidPromise,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "우리 아이 약속",
      type: "promise",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-ParentSessionInfo" */ "pages/LoginLayout/Dashboard/ParentSessionInfo.vue"
      ),
  },
  {
    path: "/dashboard/todaySession/:isPromise/:task/:index",
    name: LoginRoutesConst.MyKidContent,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-ParentSessionInfoContent.vue" */ "pages/LoginLayout/Dashboard/ParentSessionInfo/ParentSessionInfoContent.vue"
      ),
  },
  {
    path: "/dashboard/mykidstate",
    name: LoginRoutesConst.MyKidState,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "우리 아이 행동",
      type: "state",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-ParentSessionInfo" */ "pages/LoginLayout/Dashboard/ParentSessionInfo.vue"
      ),
  },
  {
    path: "/dashboard/howtouse",
    name: LoginRoutesConst.HowToUse,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "사용 방법 보기",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-HowToUse" */ "pages/LoginLayout/Dashboard/HowToUse.vue"
      ),
  },
  {
    path: "/dashboard/survey/:week",
    name: LoginRoutesConst.MyKidSurvey,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "우리 아이 설문조사",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-MyKidSurvey" */ "pages/LoginLayout/Dashboard/MyKidSurvey.vue"
      ),
  },
  {
    path: "/useForme",
    name: LoginRoutesConst.UseForme,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    meta: {
      title: "뽀미 사용",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-UseForme" */ "pages/LoginLayout/UseForme.vue"
      ),
  },

  {
    path: "/addKid/:step",
    name: LoginRoutesConst.AddFirstKid,
    props: true,
    beforeEnter: (to, from, next) => hasKids(false, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-AddKid" */ "pages/LoginLayout/kid/AddKid.vue"
      ),
  },
  {
    path: "/mykid",
    name: LoginRoutesConst.MyKid,
    meta: {
      title: "우리 아이",
    },
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-MyKid" */ "pages/LoginLayout/MyKid.vue"
      ),
  },
  {
    path: "/mykid/:type",
    name: LoginRoutesConst.MyKidReport,
    props: true,
    meta: {
      title: "우리 아이",
    },
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-MyKidReport" */ "pages/LoginLayout/MyKid/MyKidReport.vue"
      ),
  },
  {
    path: "/myPage/addKid/:step",
    name: LoginRoutesConst.AddAdditionalKid,
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-AddKid" */ "pages/LoginLayout/kid/AddKid.vue"
      ),
  },
  {
    path: "/myPage/account/:kidUUID?",
    name: LoginRoutesConst.MyPage,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,
    meta: {
      title: "마이페이지",
    },
    component: () =>
      import(
        /* webpackChunkName: "views-MyPage" */ "pages/LoginLayout/MyPage.vue"
      ),
  },
  {
    path: "/myPage/extension/:kidUUID",
    name: LoginRoutesConst.ExtensionCode,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,

    component: () =>
      import(
        /* webpackChunkName: "views-ExtensionCode" */ "pages/LoginLayout/MyPage/extension/ExtensionCode.vue"
      ),
  },
  {
    path: "/myPage/extension/register/:kidUUID",
    name: LoginRoutesConst.RegisterCode,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-RegisterCode" */ "pages/LoginLayout/MyPage/extension/ExtensionCode/RegisterCode.vue"
      ),
  },
  {
    path: "/myPage/extension/callback",
    name: LoginRoutesConst.CallbackExtensionCode,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),

    component: () =>
      import(
        /* webpackChunkName: "views-CallbackExtensionCode" */ "pages/LoginLayout/MyPage/extension/CallbackExtensionCode.vue"
      ),
  },
  {
    path: "/myPage/extension/cancel/:lCode",
    name: LoginRoutesConst.CancelExtensionCode,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-CancelExtensionCode" */ "pages/LoginLayout/MyPage/extension/CancelExtensionCode.vue"
      ),
  },

  {
    path: "/myPage/accountManagement/:isNew?",
    name: LoginRoutesConst.AccountManagement,
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-AccountManagement" */ "pages/LoginLayout/MyPage/AccountManagement.vue"
      ),
    meta: {
      title: "계정 관리",
    },
  },
  {
    path: "/myPage/accountManagement/deleteKid",
    name: LoginRoutesConst.DeleteKid,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-DeleteKid" */ "pages/LoginLayout/MyPage/AccountManagement/DeleteKid.vue"
      ),
  },
  {
    path: "/myPage/accountManagement/patchKid/:kidUUID",
    name: LoginRoutesConst.PatchKid,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    props: true,
    component: () =>
      import(
        /* webpackChunkName: "views-PatchKid" */ "pages/LoginLayout/MyPage/AccountManagement/PatchKid.vue"
      ),
  },
  {
    path: "/myPage/billing/history",
    name: LoginRoutesConst.BillingHistory,
    beforeEnter: (to, from, next) => hasKids(true, to, from, next),
    component: () =>
      import(
        /* webpackChunkName: "views-BillingHistory" */ "pages/LoginLayout/MyPage/BillingHistory.vue"
      ),
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: () => import("layouts/LayoutsIndex.vue"),
    name: "LayoutsIndex",

    children: [...LogoutRoutes, ...LoginRoutes, ...CommonRoutes],
  },
];

export default routes;
